import { NgIf, NgFor, AsyncPipe, LowerCasePipe, DatePipe } from '@angular/common';
import { Component, Inject } from '@angular/core';

import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import {
  MatNavList,
  MatListItem,
  MatListItemIcon,
  MatListItemAvatar,
  MatListItemTitle,
  MatListItemLine,
  MatListItemMeta,
  MatDivider,
} from '@angular/material/list';
import { Router } from '@angular/router';
import { CoreSelectors } from '@next/core-lib';
import { RootState } from '@next/core-lib/app';
import { PageToolbarComponent } from '@next/core-lib/components';
import { DIALOG_RETURN_TYPES, DialogService } from '@next/core-lib/dialog';
import { LocaleService, TranslatePipe, AgrNumberPipe } from '@next/core-lib/i18n';
import { BreakpointService, BreakpointServiceInjector } from '@next/core-lib/responsive';
import { WrapperSiteSidenavActions } from '@next/core-lib/wrapper-site-sidenav';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { skipWhile, take } from 'rxjs/operators';

import { Arrival } from '../../models/cycle.model';
import * as cycleActions from '../../store/actions/cycle.actions';

import { getSelectedCycle } from '../../store/selectors/cycle.selectors';
import { ArrivalItemDialogComponent } from '../arrival-item-dialog/arrival-item-dialog.component';

@Component({
  selector: 'app-arrival-overview',
  templateUrl: './arrival-overview.component.html',
  imports: [
    PageToolbarComponent,
    NgIf,
    MatNavList,
    NgFor,
    MatListItem,
    MatIcon,
    MatListItemIcon,
    MatListItemAvatar,
    MatListItemTitle,
    MatListItemLine,
    MatIconButton,
    MatListItemMeta,
    MatDivider,
    AsyncPipe,
    LowerCasePipe,
    DatePipe,
    TranslatePipe,
    AgrNumberPipe,
  ],
})
export class ArrivalOverviewComponent {
  readonly cycle$ = this.store.select(getSelectedCycle);
  readonly isLoading$: Observable<boolean> = this.store.select(CoreSelectors.selectLoading);

  constructor(
    private readonly store: Store<RootState>,
    private readonly router: Router,
    @Inject(BreakpointServiceInjector)
    private readonly breakpoint: BreakpointService,
    private readonly dialog: DialogService,
    private readonly localeService: LocaleService,
  ) {
    this.store.dispatch(cycleActions.loadHatcheriesRequest());
  }

  add() {
    this.cycle$.pipe(take(1)).subscribe((cycle) => {
      this.router.navigate(['/cycle', cycle.id, 'arrivals', 'add']);
      this.breakpoint.ltLg$.pipe(take(1)).subscribe((isMobile) => {
        if (isMobile) {
          this.store.dispatch(WrapperSiteSidenavActions.closeSidenav());
        }
      });
    });
  }

  addDialog() {
    this.store.dispatch(
      cycleActions.setSelectedArrivalId({
        id: undefined,
      }),
    );
    this.cycle$
      .pipe(
        skipWhile((c) => !c?.id),
        take(1),
      )
      .subscribe((cycle) => {
        this.dialog
          .form(ArrivalItemDialogComponent, {
            confirmButton: true,
            confirmButtonText: this.localeService.translate('add'),
            pushHistoryState: false,
            arrivalId: null,
          })
          .subscribe((result) => {
            if (result.status === DIALOG_RETURN_TYPES.confirm) {
              const nextArrivalId =
                cycle.arrivals && cycle.arrivals.length > 0
                  ? Math.max(...cycle.arrivals.map((d) => d.id)) + 1
                  : 1;
              this.store.dispatch(
                cycleActions.addArrival({
                  cycleId: cycle.id,
                  arrival: { ...result.result.arrival, id: nextArrivalId },
                }),
              );
            }
          });
      });
  }

  openArrivalEdit(arrivalId: number) {
    this.store.dispatch(
      cycleActions.setSelectedArrivalId({
        id: arrivalId,
      }),
    );
    this.cycle$.pipe(take(1)).subscribe((cycle) => {
      this.dialog
        .form(ArrivalItemDialogComponent, {
          confirmButton: true,
          confirmButtonText: this.localeService.translate('save'),
          pushHistoryState: false,
          arrivalId,
        })
        .subscribe((result) => {
          if (result.status === DIALOG_RETURN_TYPES.confirm) {
            this.store.dispatch(
              cycleActions.editArrival({ cycleId: cycle.id, arrival: result.result.arrival }),
            );
          }
          this.store.dispatch(
            cycleActions.setSelectedArrivalId({
              id: undefined,
            }),
          );
        });
    });
  }

  delete(event: Event, arrival: Arrival) {
    event.preventDefault();
    event.stopImmediatePropagation();
    this.cycle$.pipe(take(1)).subscribe((cycle) => {
      if (arrival && arrival.id) {
        this.store.dispatch(
          cycleActions.deleteArrivalWithConfirmation({ cycleId: cycle.id, id: arrival.id }),
        );
      }
    });
  }

  sort(arrivals: Array<Arrival>): Array<Arrival> {
    return arrivals.slice().sort((a, b) => (a.arrivalDate < b.arrivalDate ? -1 : 0));
  }
}
